<template>
  <div class="img_center">
    <section
      :id="mainSplideId"
      ref="mainSplide"
      class="splide"
    >
      <div class="splide__track">
        <ul class="splide__list">
          <li
            v-for="(imageUrl, index) in imageUrls"
            :key="mainSplideId + '-image-' + index"
            class="splide__slide"
          >
            <img :src="imageUrl" alt="Product Image" />
          </li>
        </ul>
      </div>
    </section>

    <section
      :id="thumbnailSplideId"
      ref="thumbnailSplide"
      class="splide"
    >
      <div class="splide__track">
        <ul class="splide__list">
          <li
            v-for="(imageUrl, index) in imageUrls"
            :key="thumbnailSplideId + '-thumb-' + index"
            class="splide__slide"
          >
            <img :src="imageUrl" alt="Product Image" />
          </li>
        </ul>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  props: {
    product: {
      type: Object,
      required: true,
    },
    instanceId: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      mainSplideInstance: null,
      thumbnailSplideInstance: null,
      localUid: `pic-carousel-${Math.random().toString(36).slice(2)}`,
    };
  },

  computed: {
    carouselUid() {
      const rawUid = this.instanceId || `${this.localUid}-${this.getProductId(this.product)}`;
      return String(rawUid).replace(/[^A-Za-z0-9_-]/g, "-");
    },

    mainSplideId() {
      return `${this.carouselUid}-main`;
    },

    thumbnailSplideId() {
      return `${this.carouselUid}-thumbs`;
    },

    imageUrls() {
      const urls = [];

      if (this.product?.bild_url) {
        urls.push(this.product.bild_url);
      }

      if (this.product?.picVariants) {
        const variantUrls = String(this.product.picVariants)
          .split(";")
          .map(url => url.trim())
          .filter(Boolean);

        urls.push(...variantUrls);
      }

      return [...new Set(urls)];
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.initSplide();
    });
  },

  beforeUnmount() {
    this.destroySplide();
  },

  watch: {
    product: {
      deep: true,
      handler() {
        this.reinitSplide();
      },
    },

    instanceId() {
      this.reinitSplide();
    },
  },

  methods: {
    getProductId(product) {
      return String(
        product?.id ??
        product?.asin ??
        product?.amazon_url ??
        product?.amazonNamenSuche ??
        ""
      );
    },

    reinitSplide() {
      this.$nextTick(() => {
        this.destroySplide();
        this.initSplide();
      });
    },

    destroySplide() {
      if (this.mainSplideInstance) {
        this.mainSplideInstance.destroy(true);
        this.mainSplideInstance = null;
      }

      if (this.thumbnailSplideInstance) {
        this.thumbnailSplideInstance.destroy(true);
        this.thumbnailSplideInstance = null;
      }
    },

    initSplide() {
      if (!this.$refs.mainSplide || !this.$refs.thumbnailSplide) {
        return;
      }

      if (!this.imageUrls.length) {
        return;
      }

      this.mainSplideInstance = new Splide(this.$refs.mainSplide, {
        width: "250px",
        height: "200px",
        type: "fade",
        rewind: true,
        pagination: false,
        arrows: false,
      });

      this.thumbnailSplideInstance = new Splide(this.$refs.thumbnailSplide, {
        fixedWidth: 48,
        fixedHeight: 40,
        gap: 2,
        rewind: true,
        pagination: false,
        isNavigation: true,
        arrows: false,
        breakpoints: {
          1920: {
            width: "250px",
            fixedWidth: 48,
            fixedHeight: 40,
            arrows: false,
          },
          2560: {
            width: "300px",
            fixedWidth: 60,
            fixedHeight: 50,
            arrows: false,
          },
        },
      });

      this.mainSplideInstance.sync(this.thumbnailSplideInstance);
      this.mainSplideInstance.mount();
      this.thumbnailSplideInstance.mount();
    },
  },
};
</script>

<style scoped>
.img_center {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.splide img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
</style>
